import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './hooks/useAuth';
import { useLocale } from './hooks/useLocale';
import { SidebarProvider } from './contexts/SidebarContext';
import { CalendarProvider } from './contexts/CalendarContext';
import { RedirectPathService } from './services/auth/RedirectPathService';
import './App.css';

// Layout Components
import Header from './components/Layout/Header';
import Sidebar from './components/Interactive/Sidebar';

// Utility Components
import { Loading } from './components/Common/StyledComponents';
import ProtectedRoute from './components/Auth/ProtectedRoute';
import ErrorBoundary from './components/Utility/ErrorBoundary';
import GlobalApiError from './components/Utility/GlobalApiError';
import ApiErrorInitializer from './components/Utility/ApiErrorInitializer';

// Page Components (lazy loaded)
const StartScreen = lazy(() => import('./pages/StartScreen'));
const LoginPage = lazy(() => import('./pages/Auth/Login'));
const ProfilePage = lazy(() => import('./pages/Profile'));
const CalendarPage = lazy(() => import('./pages/Calendar'));
const IncomePage = lazy(() => import('./pages/Income'));
const NotFoundPage = lazy(() => import('./pages/NotFound'));

function App() {
  const { isAuthenticated } = useAuth();
  const { locale } = useLocale();
  
  const getAuthenticatedRedirect = () => {
    return RedirectPathService.getRedirectPath('/calendar');
  };

  return (
    <ErrorBoundary title="Application Error" subtitle="The Teacher Web application encountered an unexpected error.">
      <Router>
        <CalendarProvider>
          <SidebarProvider>
            <div className="app" data-locale={locale}>
              <ApiErrorInitializer>
                <GlobalApiError />
              </ApiErrorInitializer>
            <Routes>
              {/* Public Routes - Full screen layout */}
              <Route 
                path="/" 
                element={
                  isAuthenticated ? <Navigate to={getAuthenticatedRedirect()} replace /> : <Navigate to="/login" replace />
                } 
              />
              
              <Route 
                path="/liff" 
                element={
                  <ErrorBoundary level="page" title="LIFF Screen Error">
                    <Suspense fallback={<Loading type="fullscreen" size="large" tip="Loading LIFF..." />}>
                      <StartScreen />
                    </Suspense>
                  </ErrorBoundary>
                } 
              />
              
              <Route 
                path="/login" 
                element={
                  isAuthenticated ? <Navigate to={getAuthenticatedRedirect()} replace /> : (
                    <ErrorBoundary level="page" title="Login Error">
                      <Suspense fallback={<Loading type="fullscreen" size="large" tip="Loading login page..." />}>
                        <LoginPage />
                      </Suspense>
                    </ErrorBoundary>
                  )
                } 
              />
              
              {/* Protected Routes - App layout with header/navigation */}
              <Route 
                path="/*" 
                element={
                  <ErrorBoundary level="page" title="Navigation Error">
                    <div className="app-layout">
                      <ErrorBoundary level="component" title="Header Error">
                        <Header />
                      </ErrorBoundary>
                      <ErrorBoundary level="component" title="Sidebar Error">
                        <Sidebar />
                      </ErrorBoundary>
                      <main className="app-main">
                        <Routes>
                          <Route 
                            path="/profile" 
                            element={
                              <ProtectedRoute>
                                <ErrorBoundary level="page" title="Profile Page Error">
                                  <Suspense fallback={<Loading type="fullpage" size="large" tip="Loading profile..." />}>
                                    <ProfilePage />
                                  </Suspense>
                                </ErrorBoundary>
                              </ProtectedRoute>
                            } 
                          />
                          
                          <Route 
                            path="/calendar" 
                            element={
                              <ProtectedRoute>
                                <ErrorBoundary level="page" title="Calendar Page Error">
                                  <Suspense fallback={<Loading type="fullpage" size="large" tip="Loading calendar..." />}>
                                    <CalendarPage />
                                  </Suspense>
                                </ErrorBoundary>
                              </ProtectedRoute>
                            } 
                          />
                          
                          
                          <Route 
                            path="/income" 
                            element={
                              <ProtectedRoute>
                                <ErrorBoundary level="page" title="Income Page Error">
                                  <Suspense fallback={<Loading type="fullpage" size="large" tip="Loading income page..." />}>
                                    <IncomePage />
                                  </Suspense>
                                </ErrorBoundary>
                              </ProtectedRoute>
                            } 
                          />
                          

                          
                          {/* 404 Route */}
                          <Route 
                            path="*" 
                            element={
                              <Suspense fallback={<Loading type="fullpage" size="large" tip="Loading page..." />}>
                                <NotFoundPage />
                              </Suspense>
                            } 
                          />
                        </Routes>
                      </main>
                    </div>
                  </ErrorBoundary>
                } 
              />
            </Routes>
            </div>
          </SidebarProvider>
        </CalendarProvider>
      </Router>
    </ErrorBoundary>
  );
}

export default App;
